/**
 * GDPR Score Calculation
 * Combined 50% SA + 50% Controls only when assessmentState === COMPLETE.
 * Incomplete states expose assessmentState so UI does not treat partial as final compliance.
 */

export type GdpControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';

export type GdprAssessmentState =
  | 'NOT_STARTED'
  | 'SELF_ASSESSMENT_ONLY'
  | 'CONTROLS_ONLY'
  | 'IN_PROGRESS'
  | 'COMPLETE';

const STATUS_TO_PERCENT: Record<GdpControlStatus, number | null> = {
  IMPLEMENTED: 100,
  PARTIAL: 50,
  NOT_IMPLEMENTED: 0,
  N_A: null,
};

export const GDPR_ASSESSMENT_STATE_LABELS: Record<
  GdprAssessmentState,
  { en: string; es: string; ru: string }
> = {
  NOT_STARTED: {
    en: 'Assessment not started',
    es: 'Evaluación no iniciada',
    ru: 'Оценка не начата',
  },
  SELF_ASSESSMENT_ONLY: {
    en: 'Self-assessment completed; controls pending',
    es: 'Autoevaluación completada; controles pendientes',
    ru: 'Самооценка завершена; контроли ожидают заполнения',
  },
  CONTROLS_ONLY: {
    en: 'Controls assessed; questionnaire pending',
    es: 'Controles evaluados; cuestionario pendiente',
    ru: 'Контроли оценены; анкета ожидает заполнения',
  },
  IN_PROGRESS: {
    en: 'Assessment in progress',
    es: 'Evaluación en curso',
    ru: 'Оценка в процессе',
  },
  COMPLETE: {
    en: 'Assessment complete',
    es: 'Evaluación completa',
    ru: 'Оценка завершена',
  },
};

function normalizeStatus(s: string | undefined | null): GdpControlStatus | null {
  if (!s || typeof s !== 'string') return null;
  const u = s.toUpperCase().replace(/\s+/g, '_');
  if (u === 'IMPLEMENTED' || u === 'PARTIAL' || u === 'NOT_IMPLEMENTED' || u === 'N_A' || u === 'NA') {
    return (u === 'NA' ? 'N_A' : u) as GdpControlStatus;
  }
  return null;
}

export function computeGdprControlsScore(controlsAnswers: Record<string, string>): {
  score: number;
  breakdown: { implemented: number; partial: number; notImplemented: number; na: number; totalNonNA: number };
} {
  const breakdown = { implemented: 0, partial: 0, notImplemented: 0, na: 0, totalNonNA: 0 };
  let sum = 0;

  for (const raw of Object.values(controlsAnswers)) {
    const status = normalizeStatus(raw);
    if (!status) continue;

    if (status === 'N_A') {
      breakdown.na++;
      continue;
    }

    breakdown.totalNonNA++;
    const pct = STATUS_TO_PERCENT[status];
    if (pct !== null) sum += pct;

    if (status === 'IMPLEMENTED') breakdown.implemented++;
    else if (status === 'PARTIAL') breakdown.partial++;
    else breakdown.notImplemented++;
  }

  const score =
    breakdown.totalNonNA > 0 ? Math.round((sum / (breakdown.totalNonNA * 100)) * 100) : 0;

  return { score, breakdown };
}

/**
 * Legacy helper. Prefer computeGdprAssessment for new callers.
 * Does not average a missing side as zero.
 */
export function computeGdprScore(params: {
  selfAssessmentScore: number | null | undefined;
  controlsScore: number | null | undefined;
}): number {
  const saPresent = typeof params.selfAssessmentScore === 'number' && Number.isFinite(params.selfAssessmentScore);
  const ctrlPresent = typeof params.controlsScore === 'number' && Number.isFinite(params.controlsScore);
  if (!saPresent && !ctrlPresent) return 0;
  if (saPresent && ctrlPresent) {
    return Math.round(
      0.5 * Math.max(0, Math.min(100, params.selfAssessmentScore as number)) +
        0.5 * Math.max(0, Math.min(100, params.controlsScore as number)),
    );
  }
  if (saPresent) return Math.max(0, Math.min(100, params.selfAssessmentScore as number));
  return Math.max(0, Math.min(100, params.controlsScore as number));
}

export type GdprAssessmentResult = {
  assessmentState: GdprAssessmentState;
  assessmentStateLabels: { en: string; es: string; ru: string };
  isFinal: boolean;
  scoreIsFinal: boolean;
  completeness: {
    selfAssessmentStarted: boolean;
    controlsStarted: boolean;
    selfAssessmentComplete: boolean;
    controlsComplete: boolean;
    selfAssessmentAnsweredApplicable: number;
    controlsAnsweredApplicable: number;
    selfAssessmentAnsweredTotal: number;
    controlsAnsweredTotal: number;
  };
  selfAssessmentScore: number | null;
  controlsScore: number | null;
  combinedScore: number | null;
  displayScore: number | null;
  /** Backward-compatible numeric field — 0 when incomplete both-sides or not started. */
  totalScore: number;
};

const DEFAULT_SA_EXPECTED = 52;
const DEFAULT_CTRL_EXPECTED = 19;

export function computeGdprAssessment(params: {
  selfAssessmentScore?: number | null;
  controlsScore?: number | null;
  selfAssessmentAnsweredApplicable?: number;
  controlsAnsweredApplicable?: number;
  selfAssessmentAnsweredTotal?: number;
  controlsAnsweredTotal?: number;
  selfAssessmentExpected?: number;
  controlsExpected?: number;
  selfAssessmentComplete?: boolean;
  controlsComplete?: boolean;
}): GdprAssessmentResult {
  const saExpected = params.selfAssessmentExpected ?? DEFAULT_SA_EXPECTED;
  const ctrlExpected = params.controlsExpected ?? DEFAULT_CTRL_EXPECTED;
  const saAnsweredAppl = Number(params.selfAssessmentAnsweredApplicable) || 0;
  const ctrlAnsweredAppl = Number(params.controlsAnsweredApplicable) || 0;
  const saAnsweredTotal = Number(params.selfAssessmentAnsweredTotal) || saAnsweredAppl;
  const ctrlAnsweredTotal = Number(params.controlsAnsweredTotal) || ctrlAnsweredAppl;

  const saStarted = saAnsweredTotal > 0;
  const ctrlStarted = ctrlAnsweredTotal > 0;

  const saComplete =
    params.selfAssessmentComplete === true ||
    (params.selfAssessmentComplete !== false && saStarted && saAnsweredTotal >= saExpected);
  const ctrlComplete =
    params.controlsComplete === true ||
    (params.controlsComplete !== false && ctrlStarted && ctrlAnsweredTotal >= ctrlExpected);

  const saHasScore =
    typeof params.selfAssessmentScore === 'number' &&
    Number.isFinite(params.selfAssessmentScore) &&
    saAnsweredAppl > 0;
  const ctrlHasScore =
    typeof params.controlsScore === 'number' &&
    Number.isFinite(params.controlsScore) &&
    ctrlAnsweredAppl > 0;

  let assessmentState: GdprAssessmentState = 'NOT_STARTED';
  if (!saStarted && !ctrlStarted) assessmentState = 'NOT_STARTED';
  else if (saStarted && !ctrlStarted) assessmentState = saComplete ? 'SELF_ASSESSMENT_ONLY' : 'IN_PROGRESS';
  else if (!saStarted && ctrlStarted) assessmentState = ctrlComplete ? 'CONTROLS_ONLY' : 'IN_PROGRESS';
  else if (saComplete && ctrlComplete) assessmentState = 'COMPLETE';
  else assessmentState = 'IN_PROGRESS';

  const selfAssessmentScore = saHasScore ? Math.max(0, Math.min(100, params.selfAssessmentScore as number)) : null;
  const controlsScore = ctrlHasScore ? Math.max(0, Math.min(100, params.controlsScore as number)) : null;
  const isFinal = assessmentState === 'COMPLETE';
  const combinedScore =
    isFinal && selfAssessmentScore != null && controlsScore != null
      ? Math.round(0.5 * selfAssessmentScore + 0.5 * controlsScore)
      : null;

  const displayScore = isFinal
    ? combinedScore
    : selfAssessmentScore != null && controlsScore == null
      ? selfAssessmentScore
      : controlsScore != null && selfAssessmentScore == null
        ? controlsScore
        : null;

  let totalScore = 0;
  if (isFinal && combinedScore != null) totalScore = combinedScore;
  else if (selfAssessmentScore != null && controlsScore == null) totalScore = selfAssessmentScore;
  else if (controlsScore != null && selfAssessmentScore == null) totalScore = controlsScore;

  return {
    assessmentState,
    assessmentStateLabels: GDPR_ASSESSMENT_STATE_LABELS[assessmentState],
    isFinal,
    scoreIsFinal: isFinal,
    completeness: {
      selfAssessmentStarted: saStarted,
      controlsStarted: ctrlStarted,
      selfAssessmentComplete: Boolean(saComplete && saStarted),
      controlsComplete: Boolean(ctrlComplete && ctrlStarted),
      selfAssessmentAnsweredApplicable: saAnsweredAppl,
      controlsAnsweredApplicable: ctrlAnsweredAppl,
      selfAssessmentAnsweredTotal: saAnsweredTotal,
      controlsAnsweredTotal: ctrlAnsweredTotal,
    },
    selfAssessmentScore,
    controlsScore,
    combinedScore,
    displayScore,
    totalScore,
  };
}
